import {
	AfterViewInit,
	Component,
	Input,
	OnDestroy,
	ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { DataSetComponent } from 'src/app/components/data-set/data-set.component';
import { Company } from 'src/app/models/data/company';
import { Device, DeviceAccessSource } from 'src/app/models/data/device';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { ApiLoadingKeys, ApiService } from 'src/app/services/api.services';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { UserSessionService } from 'src/app/services/user-session.service';
import { TransferDeviceOwnershipDialogComponent } from 'src/app/ui/device/flows/transfer-device-ownership-dialog/transfer-device-ownership-dialog.component';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetToolbarComponent,
	DataSetSearchActionGroupDirective,
	DataSetSelectedActionGroupDirective,
	DataSetActionButtonComponent,
	DataSetContentComponent,
} from '../../../../components/data-set/data-set.component';
import { DataSetSearchComponent } from '../../../../components/data-set/data-set-search.component';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import {
	DataSetTableComponent,
	DataSetTableRowSelectionComponent,
	DataSetTableMenuComponent,
} from '../../../../components/data-set/data-set-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatCheckbox } from '@angular/material/checkbox';
import { HasSystemCapabilityDirective } from '../../../../directives/system-capabilities/has-system-capability.directive';
import { OverflowChipListComponent } from '../../../../components/overflow-chip-list/overflow-chip-list.component';
import { DeviceStatusComponent } from '../../../device/device-status/device-status.component';
import { MatMenu, MatMenuContent, MatMenuItem } from '@angular/material/menu';
import { AddressPipe } from '../../../../components/pipes';
import { DataSetLoadingRowComponent } from '../../../../components/data-set/data-set-loading-row.component';

@Component({
	selector: 'app-company-devices',
	templateUrl: './company-devices.component.html',
	styleUrls: ['./company-devices.component.css', '../../../common/main.scss'],
	imports: [
		DataSetComponent_1,
		DataSetToolbarComponent,
		DataSetSearchActionGroupDirective,
		DataSetSearchComponent,
		MatFormField,
		MatLabel,
		MatInput,
		FormsModule,
		DataSetSelectedActionGroupDirective,
		DataSetActionButtonComponent,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatCheckbox,
		MatCellDef,
		MatCell,
		DataSetTableRowSelectionComponent,
		HasSystemCapabilityDirective,
		MatSortHeader,
		OverflowChipListComponent,
		DeviceStatusComponent,
		DataSetTableMenuComponent,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		MatMenu,
		MatMenuContent,
		MatMenuItem,
		AddressPipe,
		DataSetLoadingRowComponent,
	],
})
export class CompanyDevicesComponent implements AfterViewInit, OnDestroy {
	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;
	@ViewChild(MatSort, { static: false }) sort!: MatSort;
	@ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;

	@Input() company?: Company;

	displayedColumns: string[] = [
		'rowSelection',
		'serialNumber',
		'deviceLocation',
		'group',
		'propertyName',
		'address',
		'displayStatus',
		'rowMenu',
	];
	dataSource = new MatTableDataSource<{
		device: Device;
		groupNames?: string[];
	}>();
	hasTransferDeviceInventoryOwnershipCapability: boolean;
	activeCompanyId: string | undefined;

	filterLabels = {
		defaultPlaceholder: $localize`:@@companyDevices-filterDefaultPlaceholder:Search serial number...`,
		serialNumber: $localize`:@@companyDevices-filterLabelSerialNumber:Serial Number`,
		propertyName: $localize`:@@companyDevices-filterLabelPropertyName:Property Name`,
		groups: $localize`:@@companyDevices-filterLabelGroups:Group(s)`,
		status: $localize`:@@companyDevices-filterLabelStatus:Status`,
		country: $localize`:@@companyDevices-filterLabelCountry:Country`,
		address: $localize`:@@companyDevices-filterLabelAddress:Address`,
		townCity: $localize`:@@companyDevices-filterLabelTownCity:Town/City`,
		stateProvinceCounty: $localize`:@@companyDevices-filterLabelStateProvinceCounty:State/Province`,
		postalCode: $localize`:@@companyDevices-filterLabelPostalCode:Postal Code`,
	};

	private filterSubscription?: Subscription;
	private selectionSubscription?: Subscription;
	private paginationSubscription?: Subscription;
	private readonly id: string;
	private authSubscription?: Subscription;

	getDevicesByCompanyIdLoadingKey = ApiLoadingKeys.getDevicesByCompanyId;
	finishedLoadingInitData: boolean = false;

	// Reactive checkbox state properties
	isAllSelected: boolean = false;
	isIndeterminate: boolean = false;

	constructor(
		private readonly apiService: ApiService,
		private readonly route: ActivatedRoute,
		private readonly dialog: MatDialog,
		private readonly scService: SystemCapabilityService,
		private readonly userSessionService: UserSessionService,
		private readonly authService: AuthService,
		public readonly loadingService: LoadingIndicatorService
	) {
		this.id = this.route.snapshot.params.id;
		this.hasTransferDeviceInventoryOwnershipCapability = this.scService.has(
			SystemCapabilityEnum.transferDeviceInventoryOwnership
		);

		this.authSubscription = this.authService
			.userSession()
			.subscribe((session) => {
				this.activeCompanyId =
					session.assignedRole?.entityRelationship?.parentEntityId;
			});
	}

	ngAfterViewInit(): void {
		this.filterSubscription = this.dataSet.queryParams
			.pipe(
				mergeMap(async (filter) => {
					const result = await this.apiService.getDevicesByCompanyId(
						this.id,
						filter
					);
					this.finishedLoadingInitData = true;
					return result;
				})
			)
			.subscribe((data) => {
				this.dataSource.data = data;
				this.updateCheckboxState();
			});

		// Subscribe to selection changes to update checkbox state reactively
		this.selectionSubscription = this.dataSet.selectedChanged.subscribe(() => {
			this.updateCheckboxState();
		});

		// Subscribe to pagination changes to update checkbox state when switching pages
		this.paginationSubscription =
			this.dataSet.presentationFilterChanged.subscribe(() => {
				this.updateCheckboxState();
			});
	}

	ngOnDestroy(): void {
		this.filterSubscription?.unsubscribe();
		this.filterSubscription = undefined;

		this.selectionSubscription?.unsubscribe();
		this.selectionSubscription = undefined;

		this.paginationSubscription?.unsubscribe();
		this.paginationSubscription = undefined;

		this.authSubscription?.unsubscribe();
		this.authSubscription = undefined;
	}

	private updateCheckboxState(): void {
		if (!this.dataSet?.selection || !this.dataSource?.data) {
			this.isAllSelected = false;
			this.isIndeterminate = false;
			return;
		}

		// Get current page data for pagination support
		const currentPageData = this.getCurrentPageData();
		if (currentPageData.length === 0) {
			this.isAllSelected = false;
			this.isIndeterminate = false;
			return;
		}

		// Count how many items on the current page are selected
		const selectedOnCurrentPage = currentPageData.filter((item) =>
			this.dataSet.selection.isSelected(item)
		).length;

		this.isAllSelected =
			selectedOnCurrentPage > 0 &&
			selectedOnCurrentPage === currentPageData.length;
		this.isIndeterminate =
			selectedOnCurrentPage > 0 &&
			selectedOnCurrentPage < currentPageData.length;
	}

	private getCurrentPageData(): any[] {
		if (!this.dataSource?.data) {
			return [];
		}

		// Check if paginator exists and get current page data
		if (this.dataSource.paginator) {
			const startIndex =
				this.dataSource.paginator.pageIndex *
				this.dataSource.paginator.pageSize;
			const endIndex = startIndex + this.dataSource.paginator.pageSize;
			return this.dataSource.data.slice(startIndex, endIndex);
		}

		// If no paginator, return all data
		return this.dataSource.data;
	}

	isSelectedAllChecked(): boolean {
		return this.isAllSelected;
	}

	isSelectAllIntermediate(): boolean {
		return this.isIndeterminate;
	}

	onSelectAllToggle(checked: boolean): void {
		if (!this.dataSet?.selection) {
			return;
		}

		const currentPageData = this.getCurrentPageData();
		if (currentPageData.length === 0) {
			return;
		}

		if (checked) {
			// Select only items on the current page
			this.dataSet.selection.select(...currentPageData);
		} else {
			// Deselect only items on the current page
			this.dataSet.selection.deselect(...currentPageData);
		}
	}

	isInheritedDevice(device: {
		device: Device;
		source: DeviceAccessSource;
		groupNames?: string[];
	}): boolean {
		return device.source.companyId === this.activeCompanyId;
	}

	transferOwnershipClicked(): void {
		const selectedDevices = this.dataSet.selection.selected.map((device) => {
			return device.device;
		});

		this.transferDeviceOwnership(selectedDevices);
	}

	onRowMenuTransferDeviceOwnershipClicked(selectedDevice: {
		device: Device;
		groupNames?: string[];
	}): void {
		this.transferDeviceOwnership([selectedDevice.device]);
	}

	transferDeviceOwnership(selectedDevices: Device[]): void {
		this.dialog
			.open(TransferDeviceOwnershipDialogComponent, {
				panelClass: 'border-radius-dialog',
				data: {
					devices: selectedDevices,
					originalCompany: this.company,
				},
			})
			.afterClosed()
			.subscribe((_: any) => {
				this.dataSet.clearAllSelections();
				this.dataSet.applyFilters();
			});
	}

	rowId(row: { device: Device; groupNames?: string[] }): string {
		return row?.device?.id || 'unknown-id';
	}
}
